import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2, AlertTriangle, MapPin, Users, Clock, Phone, Plus } from 'lucide-react';
import AddEventDialog from './AddEventDialog';
import { API, useAuth } from '../App';
import { Button } from './ui/button';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS_STYLE = {
  scheduled: 'bg-burgundy text-white',
  pending: 'bg-gold/20 text-espresso',
  contacted: 'bg-blue-200 text-blue-900',
};
const pad = (n) => String(n).padStart(2, '0');
const keyOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

function DayCell({ day, dateKey, events, isToday, isSelected, onSelect }) {
  const scheduledCount = events.filter((e) => e.status === 'scheduled').length;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`min-h-[64px] md:min-h-[104px] p-1.5 text-left rounded-lg border transition-colors ${isSelected ? 'border-gold bg-gold/5' : 'border-cream-dark bg-white hover:bg-cream/50'}`}
      data-testid={`calendar-day-${dateKey}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-xs md:text-sm font-semibold ${isToday ? 'bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-espresso'}`}>{day}</span>
        {scheduledCount > 1 && <AlertTriangle className="w-3.5 h-3.5 text-red-600" data-testid={`double-booked-${dateKey}`} />}
      </div>
      <div className="hidden md:block space-y-1 mt-1">
        {events.slice(0, 3).map((e) => (
          <div key={e.id} className={`text-[11px] truncate rounded px-1.5 py-0.5 ${STATUS_STYLE[e.status]}`}>{e.preferred_time} {e.contact_name}</div>
        ))}
        {events.length > 3 && <div className="text-[11px] text-espresso/60">+{events.length - 3} more</div>}
      </div>
      <div className="flex md:hidden gap-0.5 mt-1 flex-wrap">
        {events.map((e) => <span key={e.id} className={`w-2 h-2 rounded-full ${e.status === 'scheduled' ? 'bg-burgundy' : e.status === 'contacted' ? 'bg-blue-400' : 'bg-gold/60'}`} />)}
      </div>
    </button>
  );
}

function DayDetails({ dateKey, events, onAdd }) {
  const label = new Date(`${dateKey}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div className="admin-card" data-testid="calendar-day-details">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-semibold text-espresso">{label}</h2>
        <Button size="sm" variant="outline" onClick={onAdd} className="gap-1.5" data-testid="add-event-on-day"><Plus className="w-4 h-4" />Add event</Button>
      </div>
      {events.length === 0 ? <p className="text-sm text-espresso/60">No events this day.</p> : (
        <div className="space-y-3">
          {events.map((e) => (
            <div key={e.id} className="border border-cream-dark rounded-lg p-3 text-sm" data-testid={`calendar-event-${e.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-semibold text-espresso">{e.contact_name}</span>
                <span className={`text-xs rounded-full px-2 py-0.5 capitalize ${STATUS_STYLE[e.status]}`}>{e.status}</span>
              </div>
              <p className="flex items-center gap-2 text-espresso/70"><Clock className="w-3.5 h-3.5 text-gold" />{e.preferred_time} · {e.event_type} · {e.package_size}</p>
              <p className="flex items-center gap-2 text-espresso/70"><Users className="w-3.5 h-3.5 text-gold" />{e.people_count} people</p>
              <p className="flex items-center gap-2 text-espresso/70"><MapPin className="w-3.5 h-3.5 text-gold" />{e.address}</p>
              {e.notes && <p className="text-espresso/60 mt-1">{e.notes}</p>}
              {e.contact_phone && <a href={`tel:${e.contact_phone}`} className="flex items-center gap-2 text-burgundy font-medium mt-1"><Phone className="w-3.5 h-3.5" />{e.contact_phone}</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCalendar() {
  const { token } = useAuth();
  const today = new Date();
  const todayKey = keyOf(today.getFullYear(), today.getMonth(), today.getDate());
  const [month, setMonth] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(todayKey);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = () => axios.get(`${API}/group-requests`, { headers: { Authorization: `Bearer ${token}` } })
    .then((res) => setRequests(res.data))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, [token]);

  const handleCreated = (dateKey) => {
    const [y, m] = dateKey.split('-').map(Number);
    setMonth({ y, m: m - 1 });
    setSelected(dateKey);
    load();
  };

  const byDate = useMemo(() => {
    const map = {};
    requests.filter((r) => STATUS_STYLE[r.status] && r.preferred_date).forEach((r) => {
      (map[r.preferred_date] = map[r.preferred_date] || []).push(r);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => (a.preferred_time || '').localeCompare(b.preferred_time || '')));
    return map;
  }, [requests]);

  const firstWeekday = new Date(month.y, month.m, 1).getDay();
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const shift = (delta) => setMonth(({ y, m }) => { const d = new Date(y, m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const title = new Date(month.y, month.m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="section-title">Event Calendar</h1>
        <Button onClick={() => setAdding(true)} className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full gap-1.5 order-last sm:order-none" data-testid="add-event-button"><Plus className="w-4 h-4" />Add Event</Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="font-semibold text-espresso min-w-[150px] text-center" data-testid="calendar-month-title">{title}</span>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="calendar-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-espresso/70">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-burgundy" />Scheduled</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gold/60" />Pending</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-400" />Contacted</span>
        <span className="flex items-center gap-1.5"><AlertTriangle className="w-3 h-3 text-red-600" />More than one scheduled event</span>
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-semibold text-espresso/60 py-1">{w}</div>)}
        {Array.from({ length: firstWeekday }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const k = keyOf(month.y, month.m, i + 1);
          return <DayCell key={k} day={i + 1} dateKey={k} events={byDate[k] || []} isToday={k === todayKey} isSelected={k === selected} onSelect={() => setSelected(k)} />;
        })}
      </div>
      <DayDetails dateKey={selected} events={byDate[selected] || []} onAdd={() => setAdding(true)} />
      <AddEventDialog open={adding} onOpenChange={setAdding} date={selected} token={token} onCreated={handleCreated} />
    </div>
  );
}
